/**
 * MM-Footer Manager - Moon Moon Agency
 * Comportements du footer :
 *  - Accordéons mobile (colonnes + Nos Magasins) : tween height via Web Animations
 *    API, groupe exclusif (un seul ouvert par `data-mm-acc`), ARIA sync,
 *    prefers-reduced-motion respecté. Desktop : toujours ouverts.
 *  - Newsletter Klaviyo : validation email + RGPD, soumission API Klaviyo Client
 *    (endpoint public /client/subscriptions/, revision 2024-10-15), messages
 *    de succès/erreur inline sans rechargement.
 */

const FooterManager = {
	config: {
		selectors: {
			root: '.mm-footer',
			acc: '[data-mm-acc]',
			accToggle: '[data-mm-acc-toggle]',
			newsletterForm: '[data-mm-footer-newsletter]',
			newsletterEmail: '#mm-footer-newsletter-email',
			newsletterRgpd: '[data-mm-footer-rgpd]',
			newsletterMessage: '[data-mm-footer-newsletter-message]',
			cookies: '[data-mm-footer-cookies]',
		},
		classes: {
			open: 'is-open',
		},
		anim: {
			openDuration: 520,
			openEasing: 'cubic-bezier(.34, 1.56, .64, 1)',
			closeDuration: 420,
			closeEasing: 'cubic-bezier(.4, 0, .2, 1)',
		},
		klaviyoEndpoint: 'https://a.klaviyo.com/client/subscriptions/',
		klaviyoRevision: '2024-10-15',
	},

	init() {
		const root = window.getElement(this.config.selectors.root);
		if (!root) return;

		this._setupAccordions(root);
		this._setupNewsletter();
		this._setupTrustRotation(root);
		this._setupCookies(root);

		window.log('info', '🦶 [FOOTER]', 'Initialized');
	},

	_setupCookies(root) {
		root.addEventListener('click', (e) => {
			if (!e.target.closest(this.config.selectors.cookies)) return;
			if (typeof window.openAxeptioCookies === 'function') window.openAxeptioCookies();
		});
	},

	/**
	 * Rotation réassurance mobile — pattern demo.
	 * L'item .is-current a une animation CSS 4s (mm-nl-trust-hold).
	 * À animationend, on avance .is-current au suivant (loop). Pas de setInterval,
	 * pas de timer JS : le timing vient du CSS, pausable via animation-play-state
	 * (au hover du parent, voir mm-footer.css).
	 * Idempotent via data-mm-nl-trust-ready.
	 */
	_setupTrustRotation(root) {
		const rot = root.querySelector('[data-mm-nl-trust]');
		if (!rot || rot.hasAttribute('data-mm-nl-trust-ready')) return;
		rot.setAttribute('data-mm-nl-trust-ready', '');

		rot.addEventListener('animationend', (e) => {
			const current = e.target.closest('.mm-footer__newsletter-reass-item');
			if (!current || !current.classList.contains('is-current')) return;
			const items = Array.from(rot.children);
			const idx = items.indexOf(current);
			if (idx === -1) return;
			const next = items[(idx + 1) % items.length];
			current.classList.remove('is-current');
			next.classList.add('is-current');
		});
	},

	/**
	 * Accordéons : desktop toujours ouverts (CSS force la visibilité), mobile
	 * fermés au first paint puis toggle click avec tween height via Web
	 * Animations API. Groupe (`data-mm-acc="col"`) — un seul ouvert par groupe
	 * en mobile pour éviter la surcharge visuelle.
	 */
	_setupAccordions(root) {
		const accordions = root.querySelectorAll(this.config.selectors.acc);
		if (!accordions.length) return;

		const isMobile = () => window.innerWidth <= (window.MOBILE_BREAKPOINT || 768);
		const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		const applyResponsiveState = () => {
			accordions.forEach((el) => {
				const body = this._getAccBody(el);
				if (!body) return;
				const alwaysAcc = this._isAlwaysAccordion(el);
				if (alwaysAcc) {
					// Reste fermé par défaut (le user ouvre au clic), aucun forçage
					if (!el.dataset.mmUserOpened) {
						el.classList.remove(this.config.classes.open);
						this._syncAria(el);
						body.style.height = '';
					}
				} else if (isMobile()) {
					if (!el.dataset.mmUserOpened) {
						el.classList.remove(this.config.classes.open);
						this._syncAria(el);
						body.style.height = '';
					}
				} else {
					el.classList.add(this.config.classes.open);
					this._syncAria(el);
					body.style.height = '';
				}
			});
		};

		applyResponsiveState();
		window.addEventListener('resize', window.debounce ? window.debounce(applyResponsiveState, 200) : applyResponsiveState);

		root.addEventListener('click', (e) => {
			const toggle = e.target.closest(this.config.selectors.accToggle);
			if (!toggle || !root.contains(toggle)) return;
			const acc = toggle.closest(this.config.selectors.acc);
			if (!acc) return;
			// Desktop : cols/storesblock inertes. Les régions (data-mm-acc="stores")
			// restent cliquables à toutes les tailles.
			if (!isMobile() && !this._isAlwaysAccordion(acc)) return;

			e.preventDefault();
			this._toggleAcc(acc, accordions, prefersReducedMotion());
		});
	},

	_getAccBody(acc) {
		return acc.querySelector('.mm-footer__column-body, .mm-footer__stores-body, .mm-footer__store-region-body');
	},

	/**
	 * Certains accordéons sont actifs à toutes les tailles (régions magasins).
	 * D'autres restent des accordéons uniquement en mobile (colonnes, storesblock).
	 */
	_isAlwaysAccordion(acc) {
		return acc.dataset.mmAcc === 'stores';
	},

	_syncAria(acc) {
		const toggle = acc.querySelector(this.config.selectors.accToggle);
		if (toggle) toggle.setAttribute('aria-expanded', String(acc.classList.contains(this.config.classes.open)));
	},

	_toggleAcc(acc, allAccordions, reducedMotion) {
		const willOpen = !acc.classList.contains(this.config.classes.open);

		if (willOpen) {
			// Ferme les autres du même groupe
			const group = acc.dataset.mmAcc;
			allAccordions.forEach((sibling) => {
				if (sibling !== acc && sibling.dataset.mmAcc === group && sibling.classList.contains(this.config.classes.open)) {
					this._closeAcc(sibling, reducedMotion);
				}
			});
			this._openAcc(acc, reducedMotion);
		} else {
			this._closeAcc(acc, reducedMotion);
		}
	},

	_openAcc(acc, reducedMotion) {
		const body = this._getAccBody(acc);
		if (!body) return;
		acc.classList.add(this.config.classes.open);
		acc.dataset.mmUserOpened = 'true';
		this._syncAria(acc);

		if (reducedMotion) {
			body.style.height = '';
			window.emit('global:content-resized', { source: 'footer' });
			return;
		}

		const target = body.scrollHeight;
		body.style.height = '0px';
		const anim = body.animate(
			[{ height: '0px' }, { height: target + 'px' }],
			{ duration: this.config.anim.openDuration, easing: this.config.anim.openEasing, fill: 'forwards' }
		);
		anim.onfinish = () => {
			body.style.height = '';
			anim.cancel();
			window.emit('global:content-resized', { source: 'footer' });
		};
	},

	_closeAcc(acc, reducedMotion) {
		const body = this._getAccBody(acc);
		if (!body) return;
		const start = body.scrollHeight;

		if (reducedMotion) {
			acc.classList.remove(this.config.classes.open);
			delete acc.dataset.mmUserOpened;
			this._syncAria(acc);
			body.style.height = '';
			window.emit('global:content-resized', { source: 'footer' });
			return;
		}

		body.style.height = start + 'px';
		const anim = body.animate(
			[{ height: start + 'px' }, { height: '0px' }],
			{ duration: this.config.anim.closeDuration, easing: this.config.anim.closeEasing, fill: 'forwards' }
		);
		acc.classList.remove(this.config.classes.open);
		delete acc.dataset.mmUserOpened;
		this._syncAria(acc);
		anim.onfinish = () => {
			body.style.height = '';
			anim.cancel();
			window.emit('global:content-resized', { source: 'footer' });
		};
	},

	_setupNewsletter() {
		const form = document.querySelector(this.config.selectors.newsletterForm);
		if (!form) return;

		form.addEventListener('submit', (e) => {
			e.preventDefault();
			this._handleNewsletterSubmit(form);
		});
	},

	async _handleNewsletterSubmit(form) {
		const emailInput = form.querySelector(this.config.selectors.newsletterEmail);
		const rgpdInput = form.querySelector(this.config.selectors.newsletterRgpd);
		const submitBtn = form.querySelector('button[type="submit"]');
		const email = emailInput.value.trim();
		const rgpdOk = rgpdInput.checked;

		this._clearMessage(form);

		if (!rgpdOk) {
			this._showMessage(form, 'error', this._i18n('rgpd_required'));
			rgpdInput.focus();
			return;
		}
		if (!email || !this._isValidEmail(email)) {
			this._showMessage(form, 'error', this._i18n('email_invalid'));
			emailInput.focus();
			return;
		}

		const companyId = form.dataset.klaviyoCompany;
		const listId = form.dataset.klaviyoList;
		if (!companyId || !listId) {
			this._showMessage(form, 'error', this._i18n('config_missing'));
			window.log('warn', '🦶 [FOOTER_NEWSLETTER]', 'Klaviyo config missing', { companyId, listId });
			return;
		}

		submitBtn.disabled = true;

		try {
			const url = `${this.config.klaviyoEndpoint}?company_id=${encodeURIComponent(companyId)}`;
			const payload = {
				data: {
					type: 'subscription',
					attributes: {
						profile: {
							data: {
								type: 'profile',
								attributes: { email },
							},
						},
						custom_source: 'Footer Newsletter',
					},
					relationships: {
						list: { data: { type: 'list', id: listId } },
					},
				},
			};
			const res = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json',
					'revision': this.config.klaviyoRevision,
				},
				body: JSON.stringify(payload),
			});

			if (res.ok || res.status === 202) {
				this._showMessage(form, 'success', this._i18n('success'));
				form.reset();
			} else {
				const errText = await res.text().catch(() => '');
				window.log('error', '🦶 [FOOTER_NEWSLETTER]', 'Klaviyo error', { status: res.status, body: errText });
				this._showMessage(form, 'error', this._i18n('error_generic'));
			}
		} catch (err) {
			window.log('error', '🦶 [FOOTER_NEWSLETTER]', 'Network error', { error: err.message });
			this._showMessage(form, 'error', this._i18n('error_generic'));
		} finally {
			submitBtn.disabled = false;
		}
	},

	_isValidEmail(value) {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
	},

	_showMessage(form, kind, text) {
		const msg = form.querySelector(this.config.selectors.newsletterMessage);
		if (!msg) return;
		msg.hidden = false;
		msg.textContent = text;
		msg.classList.remove('is-error', 'is-success');
		msg.classList.add(kind === 'error' ? 'is-error' : 'is-success');
	},

	_clearMessage(form) {
		const msg = form.querySelector(this.config.selectors.newsletterMessage);
		if (msg) {
			msg.hidden = true;
			msg.textContent = '';
			msg.classList.remove('is-error', 'is-success');
		}
	},

	/**
	 * Messages i18n injectés par le Liquid via window.MM_FOOTER_STRINGS
	 * (voir MM-Footer.liquid, script d'injection en haut de section).
	 * Si la clé est absente, retourne la clé elle-même (visible pour debug).
	 */
	_i18n(key) {
		const strings = window.MM_FOOTER_STRINGS || {};
		return strings[key] || key;
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => FooterManager.init());
} else {
	FooterManager.init();
}
